import { useEffect, useRef, useState } from "react";
import useMarketPlacesAutocomplete from "../../hooks/useMarketPlacesAutocomplete";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { GoogleMap } from "@react-google-maps/api";
import { Container, Wrapper } from "../../components/Layout/checkoutLayout";
import mapStyles from "./mapStyles";
import { Combobox, ComboboxInput, ComboboxPopover, ComboboxList, ComboboxOption } from "@reach/combobox";
import "@reach/combobox/styles.css";
import RestaurantNavBar from "../../components/RestaurantNavBar";
import CheckoutStepper from "../../components/CheckoutStepper";
import { saveShippingAddress } from "../../redux/restaurantCartSlice";
import SavedAddressPicker from "../../components/Address/SavedAddressPicker";
import { isWithinMarketBounds, MAP_CENTER, normalizeMarketLatLng } from "../../config/market";
import { resolveInitialDeliveryPhone, saveLastDeliveryPhone, accountPhone } from "../../utils/deliveryPhone";
import { updateMyProfile } from "../../api/profileApi";
import { loginSuccess } from "../../redux/userRedux";
import { toast } from "react-toastify";
import useCoverage from "../../redux/useCoverage";
import CoverageBanner from "../../components/CoverageBanner";
import GoogleMapBoundary from "../../components/Maps/GoogleMapBoundary";
import MapPin from "../../components/Maps/MapPin";
import { geocodeAddress, getPlaceSummary, latLngFromGeocode } from "../../utils/googlePlaces";
import reportClientError from "../../utils/clientTelemetry";
import {
    evaluateShippingGate,
    savedAddressLatLng,
    shippingGateLabel,
} from "../../utils/shippingValidation";
import useRestaurantAvailability from "../../hooks/useRestaurantAvailability";
import { availabilityAllowsOrdering } from "../../utils/restaurantAvailability";
import RestaurantAvailabilityBanner from "../../components/RestaurantAvailabilityBanner";

// ── Rams-style chrome, consistent with SavedAddressPicker / storefront ──────
const ACCENT = "#03660c";

const Button = styled.button`
  border: none;
  padding: 12px 18px;
  background-color: teal;
  color: white;
  cursor: pointer;
  margin-top: 10px;
  border-radius: 8px;
  font-weight: 600;
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
  &:disabled {
    background-color: #ccc;
    color: #888;
    font-weight: 400;
    font-size: 14px;
    cursor: not-allowed;
  }
`;

const Title = styled.h1`
  font-size: 22px;
  font-weight: 300;
  font-family: "Poppins", sans-serif;
  color: #000b0d;
  line-height: 1.1;
  margin: 0 0 8px;
`;

const Form = styled.form`
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  gap: 6px;
`;

const SectionLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #14282d;
  margin: 10px 0 4px;
  font-family: "Poppins", sans-serif;
`;

const Input = styled.input`
  flex: 1;
  min-width: 40%;
  margin: 2px 0;
  padding: 10px;
  border: 1px solid ${(p) => (p.$invalid ? "#c0392b" : "#d6dcde")};
  border-radius: 8px;
  outline: none;
  font-size: 14px;
  background-color: #fff;
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
  &:focus {
    border-color: ${(p) => (p.$invalid ? "#c0392b" : ACCENT)};
  }
`;

const FieldError = styled.p`
  color: #c0392b;
  font-size: 11px;
  margin: 0;
  padding: 0;
  text-align: left;
  font-style: italic;
`;

// Compact confirmation of the ONE authoritative delivery destination.
const SummaryCard = styled.div`
  border: 1.5px solid ${ACCENT};
  background: #f1f8f1;
  border-radius: 10px;
  padding: 10px 12px;
  text-align: left;
`;

const SummaryName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #14282d;
`;

const SummaryVicinity = styled.div`
  font-size: 12px;
  color: #51626a;
  line-height: 1.35;
  margin-top: 1px;
`;

const SummaryActions = styled.div`
  display: flex;
  gap: 14px;
  margin-top: 6px;
`;

const LinkAction = styled.button`
  border: none;
  background: none;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  color: ${(p) => (p.$danger ? "#c0392b" : ACCENT)};
  &:hover {
    text-decoration: underline;
  }
`;

const MapWrap = styled.div`
  margin-top: 6px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e3e7e8;
`;

const ProfileSaveErrorBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: left;
  border: 1px solid #f0b5ae;
  background: #fdf0ee;
  border-radius: 10px;
  padding: 10px 12px;
  margin: 2px 0;

  strong {
    color: #b3362a;
    font-size: 13px;
    font-weight: 600;
  }

  span {
    color: #7a4a44;
    font-size: 12px;
    line-height: 1.35;
  }
`;

const SearchHint = styled.p`
  color: #7a8a90;
  font-size: 11px;
  font-style: italic;
  margin: 2px 0 0;
  text-align: left;
`;

const mapContainerStyle = {
    width: "100%",
    height: "clamp(220px, 30vh, 280px)",
};

const RestaurantShippingContainer = styled(Container)`
  padding-bottom: 18px;
`;

const RestaurantShippingWrapper = styled(Wrapper)`
  width: min(760px, 72vw);
  padding: 16px;

  @media (max-width: 1024px) {
    width: 82vw;
  }

  @media (max-width: 768px) {
    width: 92%;
    padding: 10px;
  }
`;

const mapOptions = {
    disableDefaultUI: true,
    zoomControl: true,
    styles: mapStyles,
};

const comboInputStyle = {
    width: "100%",
    boxSizing: "border-box",
    padding: "10px",
    border: "1px solid #d6dcde",
    borderRadius: "8px",
    fontSize: "14px",
    outline: "none",
    backgroundColor: "#fff",
};

const RestaurantShippingScreen = () => {
    const {
        value,
        suggestions: { data },
        setValue,
        clearSuggestions,
        ready: placesReady,
        error: placesError,
    } = useMarketPlacesAutocomplete({ debounce: 100 });

    const [customerNumber, setCustomerNumber] = useState("");
    const [phoneError, setPhoneError] = useState("");
    const [locationId, setLocationId] = useState("");
    const [locationName, setLocationName] = useState(null); // { name, vicinity } | null
    const [latLng, setLatLng] = useState(MAP_CENTER);
    // Whether the customer has actually chosen a real delivery point (vs the
    // default map centre). Coverage only runs once a real address is set.
    const [hasChosenAddress, setHasChosenAddress] = useState(false);
    const [saveToProfile, setSaveToProfile] = useState(false);
    // ── Single selection source of truth ──
    // Which saved address (if any) the form currently mirrors. Kept in sync
    // with SavedAddressPicker via its controlled `selectedId` prop so the
    // highlighted card and the form can never diverge (the old "race").
    const [selectedSavedId, setSelectedSavedId] = useState(null);
    // Manual entry is only shown when there is no chosen address, or on demand.
    const [manualMode, setManualMode] = useState(false);
    // Map is collapsed by default; expanded on demand for pin fine-tuning.
    const [showMap, setShowMap] = useState(false);
    // Save-to-profile is now BLOCKING: we wait for the backend verdict before
    // leaving this screen, so errors like "phone already associated with
    // another account" surface HERE (inline) instead of as a toast on the
    // payment page after it's too late to react.
    const [savingPhone, setSavingPhone] = useState(false);
    const [profileSaveError, setProfileSaveError] = useState("");
    const [addressError, setAddressError] = useState("");

    const currentRestaurant = useSelector((state) => state.restaurantCart.currentRestaurant);
    const currentCart = useSelector(
        (state) => state.restaurantCart.carts[currentRestaurant] || {}
    );
    const outletId =
        Object.values(currentCart).find((item) => item?.outletId)?.outletId || null;
    const userLocation = useSelector((state) => state.userLocation);
    const currentUser = useSelector((state) => state.user?.currentUser);
    const [fetchingSuggestions, setFetchingSuggestions] = useState(false);
    const [noResultsMessage, setNoResultsMessage] = useState("");
    const timeoutRef = useRef(null);
    const typingTimer = useRef();
    const dispatch = useDispatch();
    const history = useHistory();

    // Re-gate coverage on the ENTERED delivery address (not just the browse
    // location) — this closes the abuse where a customer passes the earlier
    // add-to-cart/cart gates and then types a far address here. The backend
    // order-create 422 is still the final net; this surfaces it up front.
    const coverage = useCoverage({
        vendorId: currentRestaurant,
        outletId,
        vendorType: "restaurant",
        enabled: hasChosenAddress,
        lat: latLng?.lat,
        lng: latLng?.lng,
    });
    const availability = useRestaurantAvailability({
        vendorId: currentRestaurant,
        outletId,
    });

    // Prefill the contact phone from the customer's account (they signed up with
    // it, or have one on record). They can still change it if delivering via a
    // different number — we only prefill when the field is empty.
    const phonePrefilledRef = useRef(false);
    useEffect(() => {
        if (phonePrefilledRef.current) return;
        // Account phone first (phone-OTP signups have one); else the last
        // delivery phone this browser used (covers Google/email signups + repeat
        // customers). Empty → they type it, and we persist it on Continue.
        const prefill = resolveInitialDeliveryPhone(currentUser);
        if (prefill) {
            phonePrefilledRef.current = true;
            setCustomerNumber(String(prefill));
            setPhoneError("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser]);

    // Did the shopper EXPLICITLY choose this location, or is it a guess?
    // `locationSource: 'manual'` is set only by setManualAddress — i.e. they
    // typed and picked a place in the location modal. A gps/ip-derived location
    // is where their phone thinks they are, which is a much weaker signal about
    // where they want food delivered.
    const browseLocationIsExplicit =
        userLocation?.locationSource === "manual" &&
        isWithinMarketBounds(userLocation?.latitude, userLocation?.longitude);

    // Auto-populate the delivery address from the location the customer already
    // set (navbar / home). It runs once on mount and never overwrites a
    // saved-address pick or manual entry (both set hasChosenAddress).
    //
    // The saved-address picker USED to auto-select the primary on top of this,
    // which silently swapped a just-chosen area for a stale "Home" — the header
    // chip said one place and the form said another. An explicit pick now wins;
    // saved addresses stay one click away.
    const prefilledRef = useRef(false);
    useEffect(() => {
        if (prefilledRef.current || hasChosenAddress) return;
        const rawLat = userLocation?.latitude;
        const rawLng = userLocation?.longitude;
        if (isWithinMarketBounds(rawLat, rawLng)) {
            const lat = Number(rawLat);
            const lng = Number(rawLng);
            prefilledRef.current = true;
            setLatLng({ lat, lng });
            setLocationName({
                name: userLocation.address || "Your set location",
                vicinity: userLocation.address || "",
            });
            setValue(userLocation.address || "", false);
            setHasChosenAddress(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userLocation?.latitude, userLocation?.longitude]);

    // Refine the display name once Google resolves the picked place. (No
    // destructive cleanup here — the old version blanked the name on every
    // locationId change, causing transient empty states.)
    useEffect(() => {
        if (!locationId) return;
        getPlaceSummary(locationId)
            .then((res) => {
                setLocationName((current) => ({
                    name: res.name || current?.name || "",
                    vicinity: res.vicinity || current?.vicinity || "",
                }));
            })
            .catch((err) => {
                reportClientError(err, { area: "restaurant_place_details" });
            });
    }, [locationId]);

    useEffect(() => {
        if (placesError) {
            setAddressError(
                "Address search is temporarily unavailable. Retry the map or use a valid saved address."
            );
        }
    }, [placesError]);

    useEffect(() => {
        if (fetchingSuggestions) {
            clearTimeout(typingTimer.current);
            setNoResultsMessage("Searching...");
        } else {
            clearTimeout(typingTimer.current);
            typingTimer.current = setTimeout(() => {
                if (data.length === 0 && value.length > 0 && !locationName?.name) {
                    setNoResultsMessage("Address not found!");
                } else {
                    setNoResultsMessage("");
                }
            }, 2000);
        }
    }, [data, fetchingSuggestions, value, locationName]);

    // ── Saved address chosen (click or the picker's initial auto-select) ──
    // Populates the form as the authoritative source. NOTE: a saved address
    // without a recipient phone keeps whatever number is already in the field
    // (account prefill) instead of wiping it — that was a real lost-phone bug.
    const handleSelectSavedAddress = (addr) => {
        if (!addr) return;
        const coordinates = savedAddressLatLng(addr);
        if (!coordinates) {
            setAddressError("This saved address needs an updated location pin before checkout.");
            setHasChosenAddress(false);
            setSelectedSavedId(null);
            return;
        }
        setSelectedSavedId(addr._id || null);
        setManualMode(false);
        setAddressError("");
        if (addr.recipientPhone) {
            setCustomerNumber(addr.recipientPhone);
            setPhoneError("");
        }
        setLocationName({ name: addr.label || "Saved address", vicinity: addr.address || addr.description || "" });
        setLatLng(coordinates);
        setHasChosenAddress(true);
        setLocationId("");
        setValue(addr.address || "", false);
    };

    const clearChosenAddress = () => {
        clearSuggestions();
        setLatLng(MAP_CENTER);
        setValue("");
        setLocationId("");
        setLocationName(null);
        setHasChosenAddress(false);
        setSelectedSavedId(null);
        setShowMap(false);
        setManualMode(false);
        setAddressError("");
    };

    // The selected saved address was deleted → clear the mirrored form state
    // so stale data can't ship with the order. Phone is kept (still valid).
    const handleSavedAddressDeleted = () => {
        clearChosenAddress();
    };

    // Gate Continue on BOTH a valid phone AND a set (in-range) address — same
    // strictness as the delivery-address gate. Digit-based so "0700 771 301"
    // and "+256700771301" both count; matches the backend's ≥9-digit rule (we
    // require 10 here for the standard UG local format shown to customers).
    const hasAddress = Boolean(locationName?.name);
    const shippingGate = evaluateShippingGate({
        phone: customerNumber,
        address: locationName?.name,
        lat: latLng?.lat,
        lng: latLng?.lng,
        hasChosenAddress,
        vendorId: currentRestaurant,
        coverage,
    });
    const canContinue = shippingGate.canContinue && availability.canOrder;

    const finalizeAndContinue = () => {
        dispatch(
            saveShippingAddress({
                customerNumber,
                locationName,
                latLng,
                source: "places", // pin is the geocoder point for the picked address, not a GPS fix
                locationId,
            })
        );
        history.push("/restaurant-payment");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canContinue || savingPhone) return; // guard: phone + address + coverage must pass
        const latestAvailability = await availability.refresh();
        if (!availabilityAllowsOrdering(latestAvailability)) {
            toast.error(
                latestAvailability?.message ||
                    "This restaurant is not accepting orders right now.",
                { autoClose: false }
            );
            return;
        }
        // Remember this number so it prefills next time (esp. accounts with no
        // phone on record).
        saveLastDeliveryPhone(customerNumber);
        // Persist the number to the customer's profile BEFORE leaving this
        // screen, so failures ("phone already associated with another account",
        // network errors, …) surface inline here where the customer can still
        // react — change the number, or uncheck the box and continue without
        // saving. The ORDER itself never depends on this call succeeding.
        if (saveToProfile && customerNumber.replace(/\D/g, "").length >= 9) {
            setSavingPhone(true);
            setProfileSaveError("");
            updateMyProfile({ phoneNumber: customerNumber })
                .then((data) => {
                    // Only refresh the redux user when a token is available —
                    // dispatching loginSuccess without one falls through to the
                    // raw-payload branch of the reducer and corrupts currentUser
                    // into a nested shape (silently breaking auth headers).
                    const refreshedToken =
                        data.accessToken ||
                        currentUser?.accessToken ||
                        currentUser?.token ||
                        currentUser?.result?.accessToken ||
                        currentUser?.result?.token;
                    if (refreshedToken) {
                        dispatch(
                            loginSuccess({
                                user: { ...currentUser, ...(data.user || {}), phoneNumber: customerNumber },
                                accessToken: refreshedToken,
                            })
                        );
                    }
                    toast.success("Saved your number to your profile.");
                    setSavingPhone(false);
                    finalizeAndContinue();
                })
                .catch((err) => {
                    setSavingPhone(false);
                    setProfileSaveError(
                        err?.response?.data?.message ||
                        "Couldn't save this number to your profile."
                    );
                });
            return;
        }
        finalizeAndContinue();
    };

    const boxOptionRef = useRef();

    // Manually-typed address resolved → this becomes the authoritative
    // destination; any saved-card highlight is cleared via selectedSavedId.
    const handleSelect = async (selected) => {
        setValue(selected, false);
        clearSuggestions();

        try {
            const results = await geocodeAddress(selected);
            const raw = latLngFromGeocode(results[0]);
            const coordinates = normalizeMarketLatLng(raw.lat, raw.lng);
            if (!coordinates) throw new Error("The selected address is outside this delivery market");
            setLocationName({ name: selected, vicinity: "" });
            setLocationId(results[0].place_id);
            setLatLng(coordinates);
            setHasChosenAddress(true);
            setSelectedSavedId(null);
            setManualMode(false);
            setAddressError("");
        } catch (error) {
            reportClientError(error, { area: "restaurant_shipping_geocode" });
            setHasChosenAddress(false);
            setAddressError("We couldn’t pin that address. Choose another suggestion and retry.");
        }
    };

    const handleInput = (e) => {
        setValue(e.target.value);
        setFetchingSuggestions(true);
        setAddressError("");

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            setFetchingSuggestions(false);
        }, 100);
    };

    const renderSuggestions = () =>
        data.map((suggestion) => {
            const {
                place_id,
                structured_formatting: { main_text, secondary_text },
            } = suggestion;
            return (
                <ComboboxOption
                    key={place_id}
                    value={main_text + " " + secondary_text}
                />
            );
        });

    const handlePhoneChange = (e) => {
        const phone = e.target.value;
        setCustomerNumber(phone);
        setProfileSaveError(""); // a new number invalidates the old save error

        // Digit-based (same rule the Continue gate uses) so spaces / +256
        // formats don't false-fail.
        const digits = phone.replace(/\D/g, "");
        if (digits.length < 10) {
            setPhoneError(
                "*Phone number must be at least 10 digits long [e.g 0700123123]"
            );
        } else {
            setPhoneError("");
        }
    };

    const showManualSearch = manualMode || !hasChosenAddress;
    return (
        <>
            <RestaurantNavBar />
            <RestaurantShippingContainer>
                <RestaurantShippingWrapper>
                    <CheckoutStepper currentStep="shipping" compact />
                    <Title>SHIPPING ADDRESS</Title>
                    <SavedAddressPicker
                        onSelect={handleSelectSavedAddress}
                        onDeselect={handleSavedAddressDeleted}
                        selectedId={selectedSavedId}
                        autoSelect={!browseLocationIsExplicit}
                    />
                    <Form>
                        {/* ── ONE authoritative destination ── */}
                        <SectionLabel>Deliver to</SectionLabel>
                        {hasAddress && !showManualSearch ? (
                            <SummaryCard>
                                <SummaryName>📍 {locationName.name}</SummaryName>
                                {locationName.vicinity &&
                                    locationName.vicinity !== locationName.name && (
                                        <SummaryVicinity>{locationName.vicinity}</SummaryVicinity>
                                    )}
                                <SummaryActions>
                                    <LinkAction type="button" onClick={() => setManualMode(true)}>
                                        Use a different address
                                    </LinkAction>
                                    <LinkAction
                                        type="button"
                                        onClick={() => setShowMap((s) => !s)}
                                    >
                                        {showMap ? "Hide map" : "View on map"}
                                    </LinkAction>
                                    <LinkAction type="button" $danger onClick={clearChosenAddress}>
                                        Clear
                                    </LinkAction>
                                </SummaryActions>
                            </SummaryCard>
                        ) : (
                            <div>
                                <Combobox ref={boxOptionRef} onSelect={handleSelect}>
                                    <div style={{ position: "relative" }}>
                                        <ComboboxInput
                                            value={value}
                                            onChange={handleInput}
                                            disabled={!placesReady}
                                            placeholder={
                                                placesReady
                                                    ? "Search delivery address..."
                                                    : "Loading address search…"
                                            }
                                            style={comboInputStyle}
                                        />
                                        {addressError && <FieldError>{addressError}</FieldError>}
                                        {(fetchingSuggestions ||
                                            (data.length === 0 && value.length > 0)) &&
                                            noResultsMessage ? (
                                            <SearchHint style={{ color: "#c0392b" }}>
                                                {noResultsMessage}
                                            </SearchHint>
                                        ) : (
                                            <SearchHint>
                                                Pick a suggestion so we can pin your delivery point.
                                            </SearchHint>
                                        )}
                                    </div>
                                    <ComboboxPopover
                                        style={{
                                            backgroundColor: "white",
                                            margin: "0",
                                            padding: "0",
                                            fontSize: "13px",
                                            border: `1px solid ${ACCENT}`,
                                            boxShadow: "0 2px 10px rgba(0,0,0,0.18)",
                                            borderBottomRightRadius: "10px",
                                            borderBottomLeftRadius: "10px",
                                            zIndex: 5,
                                        }}
                                    >
                                        <ComboboxList
                                            style={{
                                                marginLeft: "10px",
                                                padding: "0",
                                                overflow: "auto",
                                                maxHeight: "200px",
                                                lineHeight: "2",
                                            }}
                                        >
                                            {renderSuggestions()}
                                        </ComboboxList>
                                    </ComboboxPopover>
                                </Combobox>
                                {hasAddress && manualMode && (
                                    <SummaryActions>
                                        <LinkAction
                                            type="button"
                                            onClick={() => setManualMode(false)}
                                        >
                                            Keep current address
                                        </LinkAction>
                                    </SummaryActions>
                                )}
                            </div>
                        )}

                        {/* Map collapsed by default; expand to fine-tune / confirm the pin */}
                        {showMap && hasChosenAddress && (
                            <MapWrap>
                                <GoogleMapBoundary height="220px">
                                    <GoogleMap
                                        mapContainerStyle={mapContainerStyle}
                                        zoom={14}
                                        center={latLng}
                                        options={mapOptions}
                                    >
                                        <MapPin position={latLng} label="Delivery location" />
                                    </GoogleMap>
                                </GoogleMapBoundary>
                            </MapWrap>
                        )}

                        {/* ── Contact phone (single field; no duplication) ── */}
                        <SectionLabel>Contact phone</SectionLabel>
                        <Input
                            value={customerNumber}
                            onChange={handlePhoneChange}
                            placeholder="Enter Contact Phone number..."
                            type="tel"
                            inputMode="tel"
                            autoComplete="tel"
                            $invalid={Boolean(phoneError)}
                            required
                        />
                        {phoneError && <FieldError>{phoneError}</FieldError>}
                        {customerNumber.replace(/\D/g, "").length >= 9 &&
                            customerNumber.trim() !== String(accountPhone(currentUser) || "").trim() && (
                                <label
                                    style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        alignItems: "center",
                                        justifyContent: "flex-start",
                                        alignSelf: "flex-start",
                                        gap: "8px",
                                        fontSize: "13px",
                                        color: "#334",
                                        margin: "4px 2px 6px",
                                        cursor: "pointer",
                                        textAlign: "left",
                                        width: "auto",
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={saveToProfile}
                                        onChange={(e) => {
                                            setSaveToProfile(e.target.checked);
                                            if (!e.target.checked) setProfileSaveError("");
                                        }}
                                        style={{
                                            width: "16px",
                                            height: "16px",
                                            minWidth: "16px",
                                            margin: 0,
                                            flex: "none",
                                            cursor: "pointer",
                                        }}
                                    />
                                    <span>Save this number to my profile for next time</span>
                                </label>
                            )}

                        {profileSaveError && (
                            <ProfileSaveErrorBox>
                                <strong>{profileSaveError}</strong>
                                <span>
                                    Use a different number, or untick “Save this
                                    number to my profile” to continue without
                                    saving it.
                                </span>
                            </ProfileSaveErrorBox>
                        )}

                        <CoverageBanner
                            variant="block"
                            deliverable={coverage.deliverable}
                            reason={coverage.reason}
                            distanceKm={coverage.distanceKm}
                            radius={coverage.radius}
                            hasLocation={coverage.hasLocation}
                            status={coverage.status}
                            error={coverage.error}
                            onRetry={coverage.refresh}
                        />
                        <RestaurantAvailabilityBanner availability={availability} />
                        <Button disabled={!canContinue || savingPhone} onClick={handleSubmit}>
                            {savingPhone
                                ? "Saving your number…"
                                : !availability.canOrder
                                  ? availability.isClosed
                                      ? "Restaurant is closed"
                                      : availability.isUnknown
                                        ? "Hours unavailable — retry above"
                                        : "Confirming restaurant hours…"
                                  : shippingGateLabel(shippingGate.code)}
                        </Button>
                    </Form>
                </RestaurantShippingWrapper>
            </RestaurantShippingContainer>
        </>
    );
};

export default RestaurantShippingScreen;
